import React from 'react';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import Spinner, { spinnerPositions } from '~c/ui/Loader';

const processOutput = (value, type) => {
  if (!value) return null;
  if (type === 'date') {
    // YYYY-MM-DD -> DD.MM.YYYY
    const splitted = value.split('-');
    return [splitted[2], splitted[1], splitted[0]].join('.');
  }

  return value;
};

const processInput = (value, type) => {
  if (!value) return null;
  if (type === 'date') {
    // DD.MM.YYYY -> YYYY-MM-DD
    const splitted = value.split('.');
    return [splitted[2], splitted[1], splitted[0]].join('-');
  }

  return value;
};

/**
 * @props {string} name
 *        {string} label
 *        {string|number} value
 *        {string} type
 *        {string} mask
 *        {string} className
 *        {boolean} error
 *        {boolean} isPending
 *        {function} onChange
 *        {function} onBlur
 *
 * @usage
 *  ```javascript
 *  <Input type="email" label="Фамилия" value="val" name="name" onChange onBlur error />
 *  ```
 */
class Input extends React.Component {
  onChangeNative = e => {
    const { onChange, type } = this.props;
    onChange(processOutput(e.target.value, type));
  };

  componentDidMount() {
    if (this.ref?.current) {
      // eslint-disable-next-line
      this.ref?.current?.addEventListener('change', this.onChangeNative);
    }
  }

  componentWillUnmount() {
    if (this.ref?.current) {
      // eslint-disable-next-line
      this.ref?.current?.removeEventListener('change', this.onChangeNative);
    }
  }

  getIcon = () => {
    const {
      required,
      value,
      validator,
      error
    } = this.props;

    if(validator && required){
      if(validator(value)){
        return <div className="inp__field--success-icon" />;
      } else {
        return <div className="inp__field--error-icon" />;
      };
    } else if(error){
      return <div className="inp__field--error-icon" />;
    }
    return false;
  }

  ref = React.createRef();

  render() {
    // CLasses
    const { props } = this;

    const classes = ['inp__field', 'inp__field--fixed-height'];
    
    if(props.validator && props.required){
      if (props.error && !(props.validator(props.value) && props.value)) classes.push('inp__field--error');
    } else if (props.error){
      classes.push('inp__field--error');
    }

    const wrapClasses = ['inp'];
    if (props.className) wrapClasses.push(props.className);

    // Masked input
    if (props.mask) {
      return (
        <label className={wrapClasses.join(' ')}>
          <InputMask
            disabled={props.disabled}
            className={classes.join(' ')}
            placeholder=" "
            name={name}
            mask={props.mask}
            onBlur={e => props.onBlur(e.target?.value)}
            onChange={e => props.onChange(e.target?.value)}
            type={props.type || 'text'}
            value={props.value || ''}
          />
          <p className="inp__label">{props.label}</p>
          { this.getIcon() }
          {props.isPending && (
            <Spinner
              className="loader--red"
              spinnerPosition={spinnerPositions.absolute}
            />
          )}
        </label>
      );
    }

    return (
      <label className={wrapClasses.join(' ')}>
        <input
          ref={this.ref}
          lang="ru"
          disabled={props.disabled}
          className={classes.join(' ')}
          type={props.type || 'text'}
          placeholder=" "
          name={props.name}
          max={props.max}
          value={processInput(props.value, props.type) || ''}
          onBlur={e => props.onBlur(processOutput(e.target.value, props.type))}
          onChange={e =>
            props.onChange(processOutput(e.target.value, props.type))
          }
        />
        <p className="inp__label">{props.label}</p>
        { this.getIcon() }
        {props.isPending && (
          <Spinner
            className="loader--red"
            spinnerPosition={spinnerPositions.absolute}
          />
        )}
      </label>
    );
  }
}

Input.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  error: PropTypes.bool,
  isPending: PropTypes.bool,
  onBlur: PropTypes.func,
  mask: PropTypes.string,
  className: PropTypes.string,
};

Input.defaultProps = {
  type: 'text',
  error: false,
  isPending: false,
  mask: '',
  value: '',
  className: '',
  onBlur: () => {},
};

export default Input;
